import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  User, 
  Calendar, 
  Clock, 
  MapPin,
  Phone,
  Pencil,
  Plus,
  Eye,
  FileText,
  Activity,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  formatDate, 
  formatCPF,
  displayCPF,
  getDaysAlertLevel, 
  STATUS_LABELS, 
  STATUS_COLORS,
  ORIGEM_LABELS,
  ALTA_MOTIVO_LABELS
} from '../lib/utils';

const VerPaciente = () => {
  const { api, isAdmin, canEditPatient, canViewFullCPF } = useAuth();
  const navigate = useNavigate();
  const { pacienteId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paciente, setPaciente] = useState(null);
  const [internacoes, setInternacoes] = useState([]);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [pacienteId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pacienteRes, internacoesRes] = await Promise.all([
        api.get(`/pacientes/${pacienteId}`),
        api.get('/internacoes?ativas=false')
      ]);
      
      setPaciente(pacienteRes.data);
      setEditData(pacienteRes.data);
      
      // Filter internacoes for this patient
      const patientInternacoes = internacoesRes.data.filter(
        (i) => i.paciente_id === pacienteId
      );
      setInternacoes(patientInternacoes);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados do paciente');
      navigate('/pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editData) return;
    
    setSaving(true);
    try {
      await api.put(`/pacientes/${pacienteId}`, editData);
      toast.success('Paciente atualizado com sucesso!');
      setPaciente(editData);
      setIsEditing(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao salvar';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData(paciente);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Card>
            <CardContent className="p-6 space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!paciente) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-muted-foreground">Paciente não encontrado</p>
          <Button onClick={() => navigate('/pacientes')} className="mt-4">
            Voltar para lista
          </Button>
        </div>
      </Layout>
    );
  }

  const activeInternacoes = internacoes.filter(i => i.status_kanban !== 'alta_efetivada');
  const completedInternacoes = internacoes.filter(i => i.status_kanban === 'alta_efetivada');

  return (
    <Layout>
      <div className="space-y-6" data-testid="ver-paciente-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/pacientes')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
                {paciente.nome}
              </h1>
              <p className="text-muted-foreground mt-1 font-mono" data-testid="ver-paciente-cpf">
                CPF: {displayCPF(paciente.cpf, canViewFullCPF)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {!isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Link to={`/pacientes/${pacienteId}/nova-internacao`}>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Internação
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Salvar
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Patient Data Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Dados Pessoais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome</Label>
                      {canEditPatient ? (
                        <Input 
                          value={editData.nome} 
                          onChange={(e) => setEditData({...editData, nome: e.target.value})}
                          data-testid="edit-nome-input"
                        />
                      ) : (
                        <>
                          <Input 
                            value={editData.nome} 
                            disabled 
                            className="bg-muted"
                          />
                          <p className="text-xs text-muted-foreground">Nome não pode ser alterado</p>
                        </>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>CPF</Label>
                      {canEditPatient ? (
                        <Input 
                          value={formatCPF(editData.cpf)} 
                          onChange={(e) => {
                            // Remove formatting and store clean CPF
                            const clean = e.target.value.replace(/\D/g, '');
                            setEditData({...editData, cpf: clean});
                          }}
                          placeholder="000.000.000-00"
                          data-testid="edit-cpf-input"
                        />
                      ) : (
                        <>
                          <Input 
                            value={displayCPF(editData.cpf, canViewFullCPF)} 
                            disabled 
                            className="bg-muted"
                          />
                          <p className="text-xs text-muted-foreground">CPF não pode ser alterado</p>
                        </>
                      )}
                    </div>
                  </div>
                  {canEditPatient && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                      Alterações em Nome e CPF serão registradas no log de auditoria.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Data de Nascimento</Label>
                      <Input 
                        type="date"
                        value={editData.data_nascimento || ''} 
                        onChange={(e) => setEditData({...editData, data_nascimento: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefone</Label>
                      <Input 
                        value={editData.telefone || ''} 
                        onChange={(e) => setEditData({...editData, telefone: e.target.value})}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>
                  <Separator />
                  <p className="text-sm font-medium">Endereço</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>CEP</Label>
                      <Input 
                        value={editData.cep || ''} 
                        onChange={(e) => setEditData({...editData, cep: e.target.value})}
                        placeholder="00000-000"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Logradouro</Label>
                      <Input 
                        value={editData.logradouro || ''} 
                        onChange={(e) => setEditData({...editData, logradouro: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Número</Label>
                      <Input 
                        value={editData.numero || ''} 
                        onChange={(e) => setEditData({...editData, numero: e.target.value})}
                      />
                    </div>
                    <div className="col-span-3 space-y-2">
                      <Label>Complemento</Label>
                      <Input 
                        value={editData.complemento || ''} 
                        onChange={(e) => setEditData({...editData, complemento: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Bairro</Label>
                      <Input 
                        value={editData.bairro || ''} 
                        onChange={(e) => setEditData({...editData, bairro: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cidade</Label>
                      <Input 
                        value={editData.cidade || ''} 
                        onChange={(e) => setEditData({...editData, cidade: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>UF</Label>
                      <Input 
                        value={editData.uf || ''} 
                        onChange={(e) => setEditData({...editData, uf: e.target.value})}
                        maxLength={2}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Nome</p>
                      <p className="font-medium">{paciente.nome}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">CPF</p>
                      <p className="font-medium font-mono">{paciente.cpf_mascarado || formatCPF(paciente.cpf)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Data de Nascimento</p>
                      <p className="font-medium">{formatDate(paciente.data_nascimento) || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Telefone</p>
                      <p className="font-medium">{paciente.telefone || '-'}</p>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Endereço
                    </p>
                    {paciente.logradouro ? (
                      <p className="font-medium">
                        {paciente.logradouro}
                        {paciente.numero && `, ${paciente.numero}`}
                        {paciente.complemento && ` - ${paciente.complemento}`}
                        <br />
                        {paciente.bairro && `${paciente.bairro}, `}
                        {paciente.cidade && `${paciente.cidade}`}
                        {paciente.uf && `/${paciente.uf}`}
                        {paciente.cep && ` - CEP: ${paciente.cep}`}
                      </p>
                    ) : (
                      <p className="text-muted-foreground">Endereço não cadastrado</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Cadastrado em</p>
                    <p className="font-medium">{formatDate(paciente.criado_em)}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Resumo de Internações
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {activeInternacoes.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Internação(ões) Ativa(s)</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">
                    {completedInternacoes.length}
                  </p>
                  <p className="text-sm text-muted-foreground">Alta(s) Realizada(s)</p>
                </div>
              </div>
              
              {paciente.observacoes && (
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">Observações</p>
                  <p className="text-sm mt-1">{paciente.observacoes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Internações List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Histórico de Internações
            </CardTitle>
            <CardDescription>
              {internacoes.length} internação(ões) registrada(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {internacoes.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">Nenhuma internação registrada</p>
                <Link to={`/pacientes/${pacienteId}/nova-internacao`}>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar primeira internação
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data Internação</TableHead>
                      <TableHead>Diagnóstico</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Dias</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {internacoes.map((internacao) => {
                      const alertLevel = getDaysAlertLevel(internacao.dias_internacao);
                      return (
                        <>
                        <TableRow key={internacao.id}>
                          <TableCell>{formatDate(internacao.data_internacao)}</TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {internacao.diagnostico_principal}
                          </TableCell>
                          <TableCell>
                            {internacao.origem ? (
                              <Badge variant="outline">
                                {ORIGEM_LABELS[internacao.origem] || internacao.origem}
                              </Badge>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline"
                              className={STATUS_COLORS[internacao.status_kanban]}
                            >
                              {STATUS_LABELS[internacao.status_kanban]}
                            </Badge>
                            {internacao.motivo_alta && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({ALTA_MOTIVO_LABELS[internacao.motivo_alta] || internacao.motivo_alta})
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline"
                              className={
                                alertLevel === 'critical' ? 'border-red-500 text-red-600' :
                                alertLevel === 'warning' ? 'border-yellow-500 text-yellow-600' : ''
                              }
                            >
                              <Clock className="h-3 w-3 mr-1" />
                              D+{internacao.dias_internacao}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Link to={`/pacientes/${pacienteId}/internacao/${internacao.id}`}>
                              <Button variant="ghost" size="sm">
                                <Eye className="h-4 w-4 mr-1" />
                                Ver
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                        {internacao.comentario_alta_administrativa && (
                          <TableRow key={`${internacao.id}-comment`} className="bg-amber-50/50 dark:bg-amber-950/10">
                            <TableCell colSpan={6}>
                              <div className="flex items-start gap-2 py-1">
                                <MessageSquare className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                                <div>
                                  <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Justificativa alta administrativa: </span>
                                  <span className="text-xs text-muted-foreground">{internacao.comentario_alta_administrativa}</span>
                                  {internacao.alta_administrativa_usuario && (
                                    <span className="text-xs text-muted-foreground ml-2">- {internacao.alta_administrativa_usuario}</span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default VerPaciente;
